import { useHasWebViewUserAgent } from 'src/hooks';
import { useCopyWindowLocationToClipboard } from './useCopyToClipboard';
import { useIsAndroidOS } from './useIsAndroidOS';
import { useIsMobileOS } from './useIsMobileOS';
import { useNavigatorShare } from './useNavigatorShare';

type useShareButtonActionProps = {
  shareUrl: string;
  title: string;
  referringItem?: string;
};

const addQueryParameterToShareUrl = (shareUrl: string): string => {
  const shareUrlWithQueryParam = new URL(shareUrl);
  shareUrlWithQueryParam.searchParams.set('source', 'share');
  return shareUrlWithQueryParam.toString();
};

export const useShareButtonAction = ({
  shareUrl,
  title,
  referringItem,
}: useShareButtonActionProps) => {
  const isMobile = useIsMobileOS();
  const isAndroid = useIsAndroidOS();
  const isWebview = useHasWebViewUserAgent();

  const shareUrlWithQueryParam = addQueryParameterToShareUrl(shareUrl);

  const copyWindowLocationToClipboard = useCopyWindowLocationToClipboard(
    shareUrlWithQueryParam,
  );
  const triggerNativeShareApi = useNavigatorShare({
    title,
    shareUrl: shareUrlWithQueryParam,
  });

  const isNativeBridgeEnabled = isMobile && isWebview;

  if (isNativeBridgeEnabled) {
    return () => {
      const showShareSheetFallback = () => {
        if (isAndroid) {
          copyWindowLocationToClipboard();
        } else {
          triggerNativeShareApi();
        }
      };

      const message = {
        id:
          Math.floor(
            (crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32) * 100,
          ) + 1,
        operation: 'share',
        body: {
          shareUrl: shareUrlWithQueryParam,
          title,
          referringItem,
        },
      };

      window.ringapp = window.ringapp || {};
      window.ringapp.onMessage = (rawMsg: string) => {
        try {
          const msg = JSON.parse(rawMsg);
          if (msg?.result === 'error') {
            showShareSheetFallback();
          }
        } catch {
          showShareSheetFallback();
        }
      };

      if (window.ringapp.sendMessage) {
        return window.ringapp.sendMessage(JSON.stringify(message));
      }
      if (isAndroid) {
        return copyWindowLocationToClipboard();
      } else {
        return triggerNativeShareApi();
      }
    };
  }

  // TODO: Find out how to trigger native share sheet from Android Webview
  // https://jira.atl.ring.com/browse/NA-3117
  if (isAndroid && isWebview) {
    return copyWindowLocationToClipboard;
  }

  if (isMobile === true) {
    return triggerNativeShareApi;
  }

  return copyWindowLocationToClipboard;
};
